import httpService from "../../services/httpService";
import { ApiEndpoints } from "../../constants/apiEndpoints";

/**
 * API responses in this backend are sometimes wrapped:
 *  - { success, message, data }
 * and sometimes returned as plain data.
 * This helper makes the client resilient to both formats.
 */
function errorMessage(error: any, fallback: string): string {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function unwrapData<T>(payload: any): T {
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data as T;
  }
  return payload as T;
}

// -------------------- Types --------------------

// User interface (aligned with backend User model)
export interface User {
  _id?: string; // MongoDB object ID (comes as _id)
  user_id: string; // Firebase UID

  email?: string;
  name?: string;

  language: string;
  score: number;

  fcm_token?: string | null;

  created_at: string;
  last_login: string;

  assigned_song_ids: string[];
  assigned_songv2_ids?: string[]; // newer model support

  devices: Device[];

  is_deleted?: boolean;
  deleted_at?: string | null;
  
  isAdmin?: boolean; // Admin yetkisi kontrolü için
}

export interface UsersPaginatedResponse {
  page: number;
  page_size: number;
  users: User[];
  total_returned: number;
}

export interface Device {
  device_id: string; // UUID
  model: string;
  hardware_version?: string;
  firmware_version?: string;
  serial_number: string;
  is_active: boolean;
  paired_at: string;
  last_connected_at: string;
}

// Request interfaces
export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  language?: string;
  score?: number;
  fcm_token?: string;

  // Admin-only convenience fields (backend may ignore if not implemented)
  assigned_song_ids?: string[];
  assigned_songv2_ids?: string[];
}

export interface AddDeviceRequest {
  model: string;
  serial_number: string;
  hardware_version?: string;
  firmware_version?: string;
  is_active?: boolean;
}

export interface UpdateDeviceRequest {
  model?: string;
  hardware_version?: string;
  firmware_version?: string;
  is_active?: boolean;
  last_connected_at?: string;
}

export interface ApiSuccessResponse {
  success: boolean;
  message: string;
}

export interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
}

// -------------------- API --------------------

// Admin - Paginated users list (GET /users/all?page=)
export const fetchUsers = async (page: number = 1): Promise<UsersPaginatedResponse> => {
  const response = await httpService.get<any>(`${ApiEndpoints.USERS_ALL}?page=${page}`);
  return unwrapData<UsersPaginatedResponse>(response.data);
};

// Admin - Get user (GET /users/{id})
export const fetchUser = async (user_id: string): Promise<User> => {
  const response = await httpService.get<any>(`/users/${user_id}`);
  return unwrapData<User>(response.data);
};

// Admin - Create user (NOT implemented in backend by default)
export const addUser = async (_user: UpdateProfileRequest): Promise<User> => {
  // Keeping function signature to avoid breaking imports, but backend typically doesn't expose POST /users.
  throw new Error("POST /users is not available in the backend. Use /users/me (auto-create on login).");
};

// Admin - Delete user (DELETE /users/{id})
export const deleteUser = async (user_id: string): Promise<void> => {
  await httpService.delete(`/users/${user_id}`);
};

// Admin - Update user (PUT /users/{id})
export const updateUser = async (user_id: string, user: UpdateProfileRequest): Promise<User> => {
  const response = await httpService.put<any>(`/users/${user_id}`, user);
  return unwrapData<User>(response.data);
};

// Current user - Profile (GET /users/me)
export const fetchUserProfile = async (): Promise<User> => {
  const response = await httpService.get<any>(ApiEndpoints.USER_PROFILE);
  return unwrapData<User>(response.data);
};

// Current user - Update profile (PUT /users/me)
export const updateUserProfile = async (user: UpdateProfileRequest): Promise<User> => {
  const response = await httpService.put<any>(ApiEndpoints.USER_PROFILE, user);
  return unwrapData<User>(response.data);
};

// Current user - Update FCM token (PUT /users/me/fcm-token)
export const updateFcmToken = async (fcm_token: string): Promise<User> => {
  const response = await httpService.put<any>(ApiEndpoints.USER_FCM_TOKEN, { fcm_token });
  return unwrapData<User>(response.data);
};

// Current user - Assigned songs (GET /users/me/songs)
export const fetchAssignedSongs = async (): Promise<any[]> => {
  const response = await httpService.get<any>(ApiEndpoints.USER_SONGS);
  return unwrapData<any[]>(response.data);
};

// Current user - Assign song to self
// Preferred: POST /users/me/songs {song_id}
// Fallback: PUT /users/me/assigned-song {song_id} (legacy)
export const assignSongToUser = async (_user_id: string, song_id: string): Promise<ApiSuccessResponse> => {
  try {
    const res = await httpService.post<any>(ApiEndpoints.USER_SONGS, { song_id });
    return unwrapData<ApiSuccessResponse>(res.data);
  } catch (error: any) {
    // fallback to legacy endpoint
    try {
      const res = await httpService.put<any>(`/users/me/assigned-song`, { song_id });
      return unwrapData<ApiSuccessResponse>(res.data);
    } catch (fallbackErr: any) {
      throw new Error(errorMessage(fallbackErr, "Song assignment failed"));
    }
  }
};

// Admin - Assign song to a user (PUT /users/{user_id}/assigned-song)
export const adminAssignSongToUser = async (user_id: string, song_id: string): Promise<ApiSuccessResponse> => {
  try {
    const response = await httpService.put<any>(`/users/${user_id}/assigned-song`, { song_id });
    return unwrapData<ApiSuccessResponse>(response.data);
  } catch (error: any) {
    throw new Error(errorMessage(error, "Admin song assignment failed"));
  }
};

// Current user - Devices list (GET /users/me/devices)
export const fetchUserDevices = async (): Promise<Device[]> => {
  const response = await httpService.get<any>(ApiEndpoints.USER_DEVICES);
  return unwrapData<Device[]>(response.data);
};

// Current user - Add device (POST /users/me/devices)
export const addUserDevice = async (data: AddDeviceRequest): Promise<Device> => {
  const response = await httpService.post<any>(ApiEndpoints.USER_DEVICES, data);
  return unwrapData<Device>(response.data);
};

// Admin - Add device to a user (PUT /users/{user_id}/device)
export const adminAddDeviceToUser = async (user_id: string, data: AddDeviceRequest): Promise<ApiSuccessResponse> => {
  try {
    const response = await httpService.put<any>(`/users/${user_id}/device`, data);
    return unwrapData<ApiSuccessResponse>(response.data);
  } catch (error: any) {
    throw new Error(errorMessage(error, "Admin device addition failed"));
  }
};

// Current user - Update device (PUT /users/me/devices/{device_id})
export const updateUserDevice = async (device_id: string, data: UpdateDeviceRequest): Promise<Device> => {
  const response = await httpService.put<any>(`${ApiEndpoints.USER_DEVICES}/${device_id}`, data);
  return unwrapData<Device>(response.data);
};

// Current user - Delete device (DELETE /users/me/devices/{device_id})
export const deleteUserDevice = async (device_id: string): Promise<void> => {
  await httpService.delete(`${ApiEndpoints.USER_DEVICES}/${device_id}`);
};

// Admin - Set admin claim for user (POST /api/admin/set-admin-claim)
export const setAdminClaim = async (email: string): Promise<ApiSuccessResponse> => {
  try {
    const response = await httpService.post<any>(ApiEndpoints.ADMIN_SET_CLAIM, { email });
    return unwrapData<ApiSuccessResponse>(response.data);
  } catch (error: any) {
    throw new Error(errorMessage(error, 'Admin yetkisi verilemedi'));
  }
};

// Admin - Remove admin claim from user (POST /api/admin/remove-admin-claim)
export const removeAdminClaim = async (email: string): Promise<ApiSuccessResponse> => {
  try {
    const response = await httpService.post<any>(ApiEndpoints.ADMIN_REMOVE_CLAIM, { email });
    return unwrapData<ApiSuccessResponse>(response.data);
  } catch (error: any) {
    throw new Error(errorMessage(error, 'Admin yetkisi kaldırılamadı'));
  }
};

// Admin - Check if current user is admin (GET /api/admin/check-admin)
export const checkAdminStatus = async (): Promise<{ isAdmin: boolean; email?: string; uid?: string }> => {
  try {
    const response = await httpService.get<any>(ApiEndpoints.ADMIN_CHECK);
    return unwrapData<{ isAdmin: boolean; email?: string; uid?: string }>(response.data);
  } catch (error: any) {
    throw new Error(errorMessage(error, 'Admin kontrolü yapılamadı'));
  }
};

// Admin - List all admin users (GET /api/admin/list-admins)
export const listAdminUsers = async (): Promise<{ uid: string; email: string; displayName?: string }[]> => {
  try {
    const response = await httpService.get<any>(ApiEndpoints.ADMIN_LIST);
    const data = unwrapData<any>(response.data);
    // Backend'den gelen format: { success, data: [...], count }
    return data.data || data || [];
  } catch (error: any) {
    throw new Error(errorMessage(error, 'Admin listesi alınamadı'));
  }
};
